import React, { useState, useEffect, useMemo } from 'react';
import { Eye, EyeOff, Save, Loader2, Users, User, Search, X, Check, Plus, Minus } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { ALL_NAV_ITEMS } from '../../components/layout/Sidebar';
import {
  useGetParametreByKeyQuery,
  useCreerParametreMutation,
  useModifierParametreMutation,
  useGetParametresQuery,
  useGetUtilisateursQuery,
} from '../../services/coreApi';

const ROLES = [
  { key: 'admin', label: 'Administrateur' },
  { key: 'agent', label: 'Agent Scolarité' },
  { key: 'agent_caisse', label: 'Agent Caisse' },
  { key: 'directeur', label: 'Directeur (lecture seule)' },
  { key: 'consultation', label: 'Consultation' },
  { key: 'surveillant', label: 'Surveillant' },
  { key: 'marketing', label: 'Marketing' },
  { key: 'bureau_stages', label: 'Bureau des stages' },
  { key: 'invite', label: 'Invité (aide examen)' },
];

type Mode = 'role' | 'user';

// Format du paramètre `onglets_visibilite_user` :
// { hidden: { [user_id]: string[] }, shown: { [user_id]: string[] } }
interface ConfigUser {
  hidden: Record<string, string[]>;
  shown: Record<string, string[]>;
}

const VisibiliteOngletsTab: React.FC = () => {
  const { data: parametres } = useGetParametresQuery();
  const { data: visibiliteParam, refetch: refetchRole } = useGetParametreByKeyQuery('onglets_visibilite');
  const { data: visibiliteUserParam, refetch: refetchUser } = useGetParametreByKeyQuery('onglets_visibilite_user');
  const { data: utilisateurs } = useGetUtilisateursQuery();
  const [creer] = useCreerParametreMutation();
  const [modifier] = useModifierParametreMutation();

  const [mode, setMode] = useState<Mode>('role');

  // ─ Config par rôle ─
  const [configRole, setConfigRole] = useState<Record<string, string[]>>({});
  const [selectedRole, setSelectedRole] = useState('agent');

  // ─ Config par utilisateur ─
  const [configUser, setConfigUser] = useState<ConfigUser>({ hidden: {}, shown: {} });
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState<string>('');

  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Hydratation configs
  useEffect(() => {
    const raw: any = (visibiliteParam as any)?.valeur_convertie ?? visibiliteParam?.valeur;
    if (!raw) return;
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (parsed && typeof parsed === 'object') {
        // Prépeupler tous les rôles connus (garde une entrée vide si absente)
        const seeded: Record<string, string[]> = {};
        ROLES.forEach(r => { seeded[r.key] = parsed[r.key] || []; });
        setConfigRole(seeded);
      }
    } catch { /* ignore */ }
  }, [visibiliteParam]);

  useEffect(() => {
    const raw: any = (visibiliteUserParam as any)?.valeur_convertie ?? visibiliteUserParam?.valeur;
    if (!raw) return;
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (parsed && typeof parsed === 'object') {
        setConfigUser({
          hidden: parsed.hidden && typeof parsed.hidden === 'object' ? parsed.hidden : {},
          shown: parsed.shown && typeof parsed.shown === 'object' ? parsed.shown : {},
        });
      }
    } catch { /* ignore */ }
  }, [visibiliteUserParam]);

  // ─ Actions rôle ─
  const toggleItemRole = (role: string, key: string) => {
    setConfigRole(prev => {
      const hidden = prev[role] || [];
      if (hidden.includes(key)) {
        return { ...prev, [role]: hidden.filter(k => k !== key) };
      }
      return { ...prev, [role]: [...hidden, key] };
    });
  };
  const toggleAllRole = (role: string, hide: boolean) => {
    setConfigRole(prev => ({
      ...prev,
      [role]: hide ? ALL_NAV_ITEMS.map(i => i.key).filter(k => k !== 'dashboard') : [],
    }));
  };

  // ─ Actions utilisateur ─
  const usersFiltres = useMemo(() => {
    const list = (utilisateurs || []).filter(u => u.role !== 'super_admin');
    return list.filter(u => {
      if (userRoleFilter && u.role !== userRoleFilter) return false;
      if (userSearch.trim()) {
        const q = userSearch.trim().toLowerCase();
        const label = `${u.nom} ${u.prenoms} ${u.username} ${u.email}`.toLowerCase();
        if (!label.includes(q)) return false;
      }
      return true;
    }).sort((a, b) => (a.nom || a.username).localeCompare(b.nom || b.username, 'fr'));
  }, [utilisateurs, userRoleFilter, userSearch]);

  const toggleSelectUser = (id: number) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedUserIds(new Set(usersFiltres.map(u => u.id)));
  };
  const clearSelection = () => setSelectedUserIds(new Set());

  // Un item est-il MASQUÉ pour TOUS les utilisateurs sélectionnés ?
  const isHiddenForAll = (key: string): boolean => {
    if (selectedUserIds.size === 0) return false;
    return Array.from(selectedUserIds).every(uid =>
      (configUser.hidden[String(uid)] || []).includes(key),
    );
  };
  // Un item est-il AJOUTÉ pour TOUS les utilisateurs sélectionnés ?
  const isShownForAll = (key: string): boolean => {
    if (selectedUserIds.size === 0) return false;
    return Array.from(selectedUserIds).every(uid =>
      (configUser.shown[String(uid)] || []).includes(key),
    );
  };

  const applyToSelected = (key: string, action: 'hide' | 'show' | 'default') => {
    if (selectedUserIds.size === 0) {
      setMessage({ type: 'error', text: 'Sélectionnez d\'abord au moins un utilisateur.' });
      return;
    }
    setConfigUser(prev => {
      const hidden = { ...prev.hidden };
      const shown = { ...prev.shown };
      Array.from(selectedUserIds).forEach(uid => {
        const key_uid = String(uid);
        const h = new Set(hidden[key_uid] || []);
        const s = new Set(shown[key_uid] || []);
        if (action === 'hide') { h.add(key); s.delete(key); }
        else if (action === 'show') { s.add(key); h.delete(key); }
        else { h.delete(key); s.delete(key); }
        if (h.size > 0) hidden[key_uid] = Array.from(h); else delete hidden[key_uid];
        if (s.size > 0) shown[key_uid] = Array.from(s); else delete shown[key_uid];
      });
      return { hidden, shown };
    });
  };

  // ─ Sauvegarde ─
  const saveParam = async (cle: string, valeur: any, description: string) => {
    const existing = (parametres || []).find((p: any) => p.cle === cle);
    if (existing) {
      await modifier({ id: existing.id, data: {
        cle, valeur: JSON.stringify(valeur), type: 'json', description,
      } }).unwrap();
    } else {
      await creer({
        cle, valeur: JSON.stringify(valeur), type: 'json', description,
      }).unwrap();
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      if (mode === 'role') {
        await saveParam('onglets_visibilite', configRole,
          'Onglets masqués par rôle');
        refetchRole();
      } else {
        await saveParam('onglets_visibilite_user', configUser,
          'Onglets masqués/ajoutés par utilisateur (surcouche)');
        refetchUser();
      }
      setMessage({ type: 'success', text: 'Configuration sauvegardée.' });
    } catch {
      setMessage({ type: 'error', text: 'Erreur de sauvegarde.' });
    }
    setSaving(false);
  };

  const hiddenForRole = configRole[selectedRole] || [];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Eye size={20} className="text-blue-500" />
            Visibilité des onglets
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Masquez des onglets pour un rôle entier, ou pour des utilisateurs
            spécifiques. Le super_admin voit toujours tout.
          </p>
        </div>
        <Button
          variant="primary"
          icon={saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          onClick={handleSave}
          disabled={saving}
        >
          Sauvegarder
        </Button>
      </div>

      {message && (
        <div className={`p-3 rounded-lg text-sm ${
          message.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-300'
            : 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300'
        }`}>
          {message.text}
        </div>
      )}

      {/* Mode restreint session d'examens : whitelist stricte pour agents */}
      <ModeRestreintAgentToggle
        parametres={parametres || []}
        onSaved={() => setMessage({ type: 'success', text: 'Mode restreint mis à jour.' })}
        onError={() => setMessage({ type: 'error', text: 'Erreur de sauvegarde.' })}
      />

      {/* Sélecteur de mode */}
      <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <button
          onClick={() => setMode('role')}
          className={`px-4 py-2 text-sm font-medium inline-flex items-center gap-2 ${
            mode === 'role'
              ? 'bg-blue-600 text-white'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          <Users size={14} /> Par rôle
        </button>
        <button
          onClick={() => setMode('user')}
          className={`px-4 py-2 text-sm font-medium inline-flex items-center gap-2 border-l border-gray-200 dark:border-gray-700 ${
            mode === 'user'
              ? 'bg-blue-600 text-white'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          <User size={14} /> Par utilisateur
        </button>
      </div>

      {mode === 'role' && (
        <>
          {/* Sélecteur de rôle */}
          <div className="flex flex-wrap gap-2">
            {ROLES.map(role => (
              <button
                key={role.key}
                onClick={() => setSelectedRole(role.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition ${
                  selectedRole === role.key
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {role.label}
                <span className="ml-1.5 text-[10px] opacity-70">
                  ({(configRole[role.key] || []).length})
                </span>
              </button>
            ))}
          </div>

          {/* Actions rapides */}
          <div className="flex gap-2">
            <button onClick={() => toggleAllRole(selectedRole, false)}
              className="text-xs px-3 py-1.5 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800 rounded-lg hover:bg-green-100">
              Tout afficher
            </button>
            <button onClick={() => toggleAllRole(selectedRole, true)}
              className="text-xs px-3 py-1.5 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100">
              Tout masquer
            </button>
          </div>

          {/* Grille des onglets */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {ALL_NAV_ITEMS.map(item => {
              const isHidden = hiddenForRole.includes(item.key);
              const isDashboard = item.key === 'dashboard';
              return (
                <button
                  key={item.key}
                  onClick={() => !isDashboard && toggleItemRole(selectedRole, item.key)}
                  disabled={isDashboard}
                  className={`flex items-center gap-3 p-3 rounded-lg border text-left transition ${
                    isDashboard
                      ? 'bg-gray-50 dark:bg-gray-700/30 border-gray-200 dark:border-gray-600 opacity-50 cursor-not-allowed'
                      : isHidden
                        ? 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800 hover:bg-red-100'
                        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-green-50'
                  }`}
                >
                  {isHidden
                    ? <EyeOff size={16} className="text-red-500 shrink-0" />
                    : <Eye size={16} className="text-green-500 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${
                      isHidden ? 'text-red-700 dark:text-red-300 line-through' : 'text-gray-900 dark:text-gray-100'
                    }`}>{item.label}</p>
                    <p className="text-[10px] text-gray-400 truncate">{item.path}</p>
                  </div>
                  {isDashboard && <span className="text-[10px] text-gray-400">Toujours visible</span>}
                </button>
              );
            })}
          </div>

          <p className="text-xs text-gray-400 dark:text-gray-500">
            Le Tableau de Bord est toujours visible pour tous les rôles.
          </p>
        </>
      )}

      {mode === 'user' && (
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4">
          {/* Liste utilisateurs */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
            <div className="p-2 border-b border-gray-200 dark:border-gray-700 space-y-2">
              <div className="relative">
                <Search size={12} className="absolute left-2 top-2 text-gray-400" />
                <input value={userSearch} onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Rechercher un utilisateur…"
                  className="w-full pl-7 pr-2 py-1.5 text-xs rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100" />
              </div>
              <select value={userRoleFilter} onChange={(e) => setUserRoleFilter(e.target.value)}
                className="w-full px-2 py-1 text-xs rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
                <option value="">Tous les rôles</option>
                {ROLES.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
              <div className="flex items-center justify-between text-[10px] text-gray-500">
                <span>
                  {selectedUserIds.size} / {usersFiltres.length} sélectionné(s)
                </span>
                <div className="flex gap-1">
                  <button onClick={selectAllVisible} className="text-blue-600 hover:underline">Tout</button>
                  <span>·</span>
                  <button onClick={clearSelection} className="text-gray-500 hover:underline">Aucun</button>
                </div>
              </div>
            </div>
            <div className="max-h-[520px] overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700/50">
              {usersFiltres.length === 0 ? (
                <div className="p-4 text-xs text-gray-500 text-center">Aucun utilisateur.</div>
              ) : usersFiltres.map(u => {
                const selected = selectedUserIds.has(u.id);
                const hiddenCount = (configUser.hidden[String(u.id)] || []).length;
                const shownCount = (configUser.shown[String(u.id)] || []).length;
                return (
                  <button
                    key={u.id}
                    onClick={() => toggleSelectUser(u.id)}
                    className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition ${
                      selected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    }`}
                  >
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      selected ? 'border-blue-500 bg-blue-500' : 'border-gray-300 dark:border-gray-600'
                    }`}>
                      {selected && <Check size={10} className="text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                        {u.nom} {u.prenoms}
                      </div>
                      <div className="text-[10px] text-gray-500 truncate">
                        {u.username} · {u.role}
                      </div>
                    </div>
                    {(hiddenCount > 0 || shownCount > 0) && (
                      <div className="flex gap-1 flex-shrink-0">
                        {hiddenCount > 0 && (
                          <span className="text-[9px] px-1 rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300" title="Onglets masqués">
                            −{hiddenCount}
                          </span>
                        )}
                        {shownCount > 0 && (
                          <span className="text-[9px] px-1 rounded bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300" title="Onglets ajoutés">
                            +{shownCount}
                          </span>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Onglets — actions */}
          <div>
            <div className="mb-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-xs text-blue-900 dark:text-blue-200">
              <div className="font-semibold mb-0.5">Comment ça marche</div>
              <ul className="list-disc pl-4 space-y-0.5">
                <li><Minus size={10} className="inline text-red-500 -mt-0.5" /> <strong>Masquer</strong> : retire cet onglet en plus du défaut du rôle.</li>
                <li><Plus size={10} className="inline text-green-500 -mt-0.5" /> <strong>Ajouter</strong> : donne cet onglet en plus du défaut du rôle (idéal pour les invités).</li>
                <li>Par défaut : le comportement du rôle s'applique.</li>
              </ul>
            </div>

            {selectedUserIds.size === 0 ? (
              <div className="text-center py-12 text-gray-500 text-sm rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
                Sélectionnez un ou plusieurs utilisateurs à gauche pour ajuster leurs onglets.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {ALL_NAV_ITEMS.map(item => {
                  const isHidden = isHiddenForAll(item.key);
                  const isShown = isShownForAll(item.key);
                  const isDashboard = item.key === 'dashboard';
                  return (
                    <div
                      key={item.key}
                      className={`flex items-center gap-2 p-2 rounded-lg border ${
                        isDashboard
                          ? 'bg-gray-50 dark:bg-gray-700/30 border-gray-200 dark:border-gray-600 opacity-60'
                          : isHidden
                            ? 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800'
                            : isShown
                              ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
                              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className={`text-xs font-medium truncate ${
                          isHidden ? 'text-red-700 dark:text-red-300 line-through' :
                          isShown ? 'text-green-700 dark:text-green-300' :
                          'text-gray-900 dark:text-gray-100'
                        }`}>{item.label}</div>
                        <div className="text-[10px] text-gray-400 truncate">{item.path}</div>
                      </div>
                      {!isDashboard && (
                        <div className="flex gap-1 flex-shrink-0">
                          <button
                            onClick={() => applyToSelected(item.key, 'hide')}
                            title="Masquer pour la sélection"
                            className={`p-1.5 rounded transition ${
                              isHidden ? 'bg-red-500 text-white' :
                              'bg-gray-100 dark:bg-gray-700 hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-600 dark:text-gray-300 hover:text-red-600'
                            }`}
                          ><EyeOff size={12} /></button>
                          <button
                            onClick={() => applyToSelected(item.key, 'show')}
                            title="Ajouter pour la sélection"
                            className={`p-1.5 rounded transition ${
                              isShown ? 'bg-green-500 text-white' :
                              'bg-gray-100 dark:bg-gray-700 hover:bg-green-100 dark:hover:bg-green-900/30 text-gray-600 dark:text-gray-300 hover:text-green-600'
                            }`}
                          ><Eye size={12} /></button>
                          <button
                            onClick={() => applyToSelected(item.key, 'default')}
                            title="Revenir au défaut du rôle"
                            className="p-1.5 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500"
                          ><X size={12} /></button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VisibiliteOngletsTab;


// ─────────────────────────────────────────────────────────────────────────────
// Mode restreint « session d'examens » — toggle simple qui applique la
// whitelist stricte AGENT_RESTREINT_EXAMEN_KEYS de la Sidebar aux comptes
// de rôle `agent` / `agent_caisse`. Utilisé pendant les examens pour éviter
// que ces agents accèdent au dépôt confidentiel des sujets, aux résultats,
// aux relevés, etc.
// ─────────────────────────────────────────────────────────────────────────────
const RUBRIQUES_AUTORISEES_LABELS = [
  'Étudiants',
  'Service Scolarité (rédaction rapports)',
  'Formateurs',
  'Association Copies',
  'Emplois du Temps',
  'Statistiques Participation Examens',
  'Chat instantané',
];

interface ModeRestreintToggleProps {
  parametres: any[];
  onSaved: () => void;
  onError: () => void;
}

const ModeRestreintAgentToggle: React.FC<ModeRestreintToggleProps> = ({ parametres, onSaved, onError }) => {
  const [creer] = useCreerParametreMutation();
  const [modifier] = useModifierParametreMutation();
  const [saving, setSaving] = useState(false);
  const [savingExceptions, setSavingExceptions] = useState(false);
  const { data: utilisateurs } = useGetUtilisateursQuery();
  const [exceptionSearch, setExceptionSearch] = useState('');

  const existing = parametres.find((p: any) => p.cle === 'mode_restreint_agent_examen');
  const currentValue = (() => {
    const raw = existing?.valeur;
    if (typeof raw === 'boolean') return raw;
    if (typeof raw === 'string') return raw === 'true' || raw === '1';
    return false;
  })();

  // Exceptions : IDs d'utilisateurs (agents) qui gardent l'accès complet
  // malgré le mode restreint. Stocké en JSON dans un paramètre séparé.
  const existingExceptions = parametres.find((p: any) => p.cle === 'mode_restreint_agent_examen_exceptions');
  const exceptionsIds: number[] = (() => {
    const raw = existingExceptions?.valeur;
    try {
      const arr = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (Array.isArray(arr)) return arr.map((x: any) => Number(x)).filter((n: number) => !Number.isNaN(n));
    } catch { /* ignore */ }
    return [];
  })();

  const toggle = async () => {
    setSaving(true);
    try {
      const nouveauValeur = currentValue ? 'false' : 'true';
      if (existing) {
        await modifier({ id: existing.id, data: {
          cle: 'mode_restreint_agent_examen', valeur: nouveauValeur,
          type: 'boolean', description: 'Restreint la sidebar des agents aux 7 rubriques essentielles pendant les examens',
        } }).unwrap();
      } else {
        await creer({
          cle: 'mode_restreint_agent_examen', valeur: nouveauValeur,
          type: 'boolean',
          description: 'Restreint la sidebar des agents aux 7 rubriques essentielles pendant les examens',
        }).unwrap();
      }
      onSaved();
    } catch {
      onError();
    }
    setSaving(false);
  };

  const saveExceptions = async (newIds: number[]) => {
    setSavingExceptions(true);
    try {
      const val = JSON.stringify(newIds);
      if (existingExceptions) {
        await modifier({ id: existingExceptions.id, data: {
          cle: 'mode_restreint_agent_examen_exceptions', valeur: val,
          type: 'json', description: 'IDs des agents exemptés du mode restreint (ex : ceux qui travaillent à la scolarité)',
        } }).unwrap();
      } else {
        await creer({
          cle: 'mode_restreint_agent_examen_exceptions', valeur: val,
          type: 'json',
          description: 'IDs des agents exemptés du mode restreint (ex : ceux qui travaillent à la scolarité)',
        }).unwrap();
      }
      onSaved();
    } catch {
      onError();
    }
    setSavingExceptions(false);
  };

  const toggleException = (uid: number) => {
    const set = new Set(exceptionsIds);
    if (set.has(uid)) set.delete(uid);
    else set.add(uid);
    saveExceptions(Array.from(set));
  };

  // On propose uniquement les agents (les autres rôles ne sont pas concernés
  // par la restriction, aucun besoin de les exempter).
  const agents = (utilisateurs || []).filter((u: any) =>
    u.role === 'agent' || u.role === 'agent_caisse'
  );
  const filteredAgents = exceptionSearch.trim()
    ? agents.filter((u: any) => {
        const q = exceptionSearch.toLowerCase();
        const full = `${u.nom || ''} ${u.prenoms || ''} ${u.username || ''} ${u.email || ''}`.toLowerCase();
        return full.includes(q);
      })
    : agents;
  const exceptionUsers = agents.filter((u: any) => exceptionsIds.includes(Number(u.id)));

  return (
    <div className={`rounded-xl border p-5 ${
      currentValue
        ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-700'
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
    }`}>
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <h3 className={`font-semibold flex items-center gap-2 ${
            currentValue ? 'text-amber-900 dark:text-amber-200' : 'text-gray-900 dark:text-gray-100'
          }`}>
            {currentValue ? <EyeOff size={18} /> : <Eye size={18} />}
            Mode restreint « Session d'examens » pour les agents
          </h3>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
            Une fois activé, tous les comptes de rôle <strong>Agent Scolarité</strong> et
            <strong> Agent Caisse</strong> ne verront que ces rubriques dans la sidebar :
          </p>
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {RUBRIQUES_AUTORISEES_LABELS.map(l => (
              <li key={l} className={`text-[11px] px-2 py-0.5 rounded ${
                currentValue
                  ? 'bg-amber-200/60 text-amber-900 dark:bg-amber-800/40 dark:text-amber-200'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}>{l}</li>
            ))}
          </ul>
          <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-500 italic">
            Empêche l'accès au dépôt confidentiel des sujets, aux résultats, relevés…
            À désactiver hors période d'examens pour rendre le fonctionnement complet.
          </p>
        </div>
        <button
          type="button"
          onClick={toggle}
          disabled={saving}
          className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors disabled:opacity-50 ${
            currentValue ? 'bg-amber-600' : 'bg-gray-300 dark:bg-gray-600'
          }`}
          role="switch"
          aria-checked={currentValue}
          title={currentValue ? 'Désactiver le mode restreint' : 'Activer le mode restreint'}
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
            currentValue ? 'translate-x-8' : 'translate-x-1'
          }`} />
          {saving && (
            <Loader2 size={12} className="absolute right-1 top-1.5 animate-spin text-white" />
          )}
        </button>
      </div>

      {/* Liste d'exceptions : agents qui gardent l'accès complet.
          Toujours affichée (même si mode restreint désactivé) pour permettre
          la préparation en amont. */}
      <div className={`mt-4 pt-4 border-t ${
        currentValue ? 'border-amber-300 dark:border-amber-700' : 'border-gray-200 dark:border-gray-700'
      }`}>
        <div className="flex items-center justify-between mb-2">
          <div>
            <h4 className={`text-sm font-semibold flex items-center gap-2 ${
              currentValue ? 'text-amber-900 dark:text-amber-200' : 'text-gray-700 dark:text-gray-300'
            }`}>
              <Users size={14} /> Exceptions — accès complet malgré le mode restreint
            </h4>
            <p className="text-[11px] text-gray-500 dark:text-gray-500 mt-0.5">
              Utile pour les agents qui travaillent directement à la scolarité et ont besoin
              de leur menu habituel (résultats, PV, brassage…). {exceptionUsers.length} exception(s).
            </p>
          </div>
          {savingExceptions && <Loader2 size={14} className="animate-spin text-amber-600" />}
        </div>

        {/* Chips des exceptions actuelles */}
        {exceptionUsers.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {exceptionUsers.map((u: any) => (
              <span
                key={u.id}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 border border-emerald-300 dark:border-emerald-700"
              >
                <Check size={10} />
                {(u.nom || '') + ' ' + (u.prenoms || '')} <span className="opacity-60">({u.username})</span>
                <button
                  onClick={() => toggleException(Number(u.id))}
                  className="ml-0.5 hover:text-emerald-950 dark:hover:text-white"
                  title="Retirer de la liste d'exceptions"
                >
                  <X size={10} />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Recherche + liste des agents à ajouter */}
        <div className="relative mb-1.5">
          <Search size={12} className="absolute left-2 top-2 text-gray-400" />
          <input
            type="text"
            value={exceptionSearch}
            onChange={(e) => setExceptionSearch(e.target.value)}
            placeholder="Rechercher un agent par nom ou identifiant..."
            className="w-full pl-7 pr-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        {agents.length === 0 ? (
          <p className="text-xs text-gray-500 italic px-2 py-2">Aucun agent trouvé dans les utilisateurs.</p>
        ) : (
          <div className="max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded bg-gray-50 dark:bg-gray-900/40">
            {filteredAgents.length === 0 ? (
              <p className="text-xs text-gray-500 italic px-2 py-2">Aucune correspondance.</p>
            ) : (
              filteredAgents.map((u: any) => {
                const checked = exceptionsIds.includes(Number(u.id));
                return (
                  <label
                    key={u.id}
                    className={`flex items-center gap-2 px-2 py-1 text-xs cursor-pointer hover:bg-white dark:hover:bg-gray-800 ${
                      checked ? 'bg-emerald-50 dark:bg-emerald-950/30' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleException(Number(u.id))}
                      className="rounded text-emerald-600"
                    />
                    <span className="flex-1 truncate">
                      <span className="text-gray-900 dark:text-gray-100">{u.nom} {u.prenoms}</span>
                      <span className="text-gray-500 ml-1">— {u.username}</span>
                      <span className="text-[10px] text-gray-400 ml-1">
                        ({u.role === 'agent_caisse' ? 'Caisse' : 'Scolarité'})
                      </span>
                    </span>
                  </label>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};
